import { FieldDescription, FormControl, FormHelperText } from '@/components/field';
import classNames from 'classnames';
import type React from 'react';
import { useId } from 'react';

import classes from './IconSelect.module.scss';

export interface IconSelectValue<T> {
  key: T;
  label: string;
  icon: React.ReactNode;
}

export interface IconSelectData<T> {
  selected: T;
  setSelected: React.Dispatch<React.SetStateAction<T>>;
  values: IconSelectValue<T>[];
  helperText?: string;
  disabled?: boolean;
  error?: boolean;
  label?: string;
}

/**
 * Usage:
 *
 * <IconSelect<number>                // <number>: The type of the key of the selected value
 *    selected={selected}             // state of the selected key
 *    setSelected={setSelected}
 *    values={values}                 // List of selection items of the type IconSelectValue
 *    radioName={'squaremeters'}      // unique key for the radio group
 * />
 *
 * @param props
 * @returns
 */
function IconSelect<T>(props: IconSelectData<T>) {
  const labelId = useId();

  const handleOnChange = (newValue: T) => {
    if (!props.disabled && newValue !== null) {
      props.setSelected(newValue);
    }
  };

  const isSelected = (key: T): boolean => JSON.stringify(key) === JSON.stringify(props.selected);

  return (
    <FormControl
      className={classes.formControl}
      data-disabled={props.disabled ? 'true' : 'false'}
      error={props.error}
      fullWidth
    >
      {props.label && (
        <span className={classes.groupLabel} id={labelId}>
          {props.label}
        </span>
      )}
      <div
        aria-disabled={props.disabled || undefined}
        aria-label={props.label ? undefined : 'Tastenauswahl'}
        aria-labelledby={props.label ? labelId : undefined}
        className={classNames(classes.buttonGroup, {
          [classes.buttonGroupDisabled]: props.disabled,
          [classes.buttonGroupError]: props.error,
        })}
        role="radiogroup"
      >
        {props.values.map((value, index) => {
          const selected = isSelected(value.key);
          return (
            <button
              key={JSON.stringify(value.key)}
              aria-checked={selected}
              aria-label={value.label}
              className={classNames(classes.iconButton, {
                [classes.iconButtonError]: props.error,
                [classes.iconButtonSelected]: selected,
              })}
              data-testid={`icon-select-toggle-button-${index + 1}`}
              disabled={props.disabled}
              role="radio"
              type="button"
              onClick={() => handleOnChange(value.key)}
            >
              {value.icon}
            </button>
          );
        })}
      </div>

      {props.helperText &&
        (props.error ? (
          <FormHelperText error>{props.helperText}</FormHelperText>
        ) : (
          <FieldDescription>{props.helperText}</FieldDescription>
        ))}
    </FormControl>
  );
}

export default IconSelect;
